import { useEffect, useRef } from "react";
import type {
	MessagePayload,
	ClientToHostMessage,
} from "@rubyplay/effw-connector";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import {
	buildFatalPayload,
	getEfRuntimeInstanceId,
	hasFatalBeenReported,
	markFatalReported,
} from "@/runtime/efRuntimeProcess";
import { debugError, debugLog } from "@/utils";

const HEARTBEAT_INTERVAL_MS = 2000;

function normalizeString(value: unknown): string {
	if (typeof value === "string") {
		return value.trim();
	}

	if (value instanceof Error) {
		return `${value.name ?? ""} ${value.message ?? ""}`.trim();
	}

	if (
		value &&
		typeof value === "object" &&
		"message" in value &&
		typeof (value as { message?: unknown }).message === "string"
	) {
		return String((value as { message: string }).message).trim();
	}

	return "";
}

function normalizeStack(value: unknown): string {
	if (value instanceof Error && typeof value.stack === "string") {
		return value.stack.trim();
	}

	if (
		value &&
		typeof value === "object" &&
		"stack" in value &&
		typeof (value as { stack?: unknown }).stack === "string"
	) {
		return String((value as { stack: string }).stack).trim();
	}

	return "";
}

function isCaptainUpSdkSource(text: string): boolean {
	if (!text) {
		return false;
	}

	const normalized = text.toLowerCase();

	return (
		normalized.includes("sdk-") ||
		normalized.includes("captainup") ||
		normalized.includes("cptup")
	);
}

function shouldIgnoreWindowError(event: ErrorEvent): boolean {
	const filename = normalizeString(event.filename);
	const stack = normalizeStack(event.error);

	return isCaptainUpSdkSource(filename) || isCaptainUpSdkSource(stack);
}

function shouldIgnoreUnhandledRejection(reason: unknown): boolean {
	const stack = normalizeStack(reason);
	return isCaptainUpSdkSource(stack);
}

function reportFatalOnce(
	sendFatalError: (
		payload: MessagePayload<ClientToHostMessage, "FatalError">
	) => void,
	payload: MessagePayload<ClientToHostMessage, "FatalError">
): void {
	if (hasFatalBeenReported()) {
		return;
	}

	markFatalReported();

	try {
		sendFatalError(payload);
	} catch (error) {
		debugError("[EF Supervisor] failed to send FatalError", error);
	}
}

export function useEfRuntimeSupervisor(runtimeEnabled: boolean): void {
	const { sendClientReady, sendHeartBeat, sendFatalError } = useCommunicator();

	const clientReadySentRef = useRef(false);

	useEffect(() => {
		const handleWindowError = (event: ErrorEvent) => {
			if (shouldIgnoreWindowError(event)) {
				return;
			}

			const error = event.error ?? event.message ?? "Unknown window error";

			debugError("[EF Supervisor] window.onerror", {
				message: normalizeString(event.message),
				filename: normalizeString(event.filename),
				stack: normalizeStack(event.error),
				error,
			});

			reportFatalOnce(sendFatalError, buildFatalPayload("RUNTIME", error));
		};

		const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
			if (shouldIgnoreUnhandledRejection(event.reason)) {
				return;
			}

			const reason = event.reason ?? "Unhandled promise rejection";

			debugError("[EF Supervisor] window.onunhandledrejection", {
				message: normalizeString(reason),
				stack: normalizeStack(reason),
				reason,
			});

			reportFatalOnce(
				sendFatalError,
				buildFatalPayload("UNHANDLED_REJECTION", reason)
			);
		};

		window.addEventListener("error", handleWindowError);
		window.addEventListener("unhandledrejection", handleUnhandledRejection);

		return () => {
			window.removeEventListener("error", handleWindowError);
			window.removeEventListener(
				"unhandledrejection",
				handleUnhandledRejection
			);
		};
	}, [sendFatalError]);

	useEffect(() => {
		if (!runtimeEnabled) {
			return;
		}

		if (clientReadySentRef.current) {
			return;
		}

		clientReadySentRef.current = true;

		const payload: MessagePayload<ClientToHostMessage, "ClientReady"> = {
			instanceId: getEfRuntimeInstanceId(),
		};

		debugLog("[EF Supervisor] send ClientReady", {
			runtimeEnabled,
			payload,
		});

		sendClientReady(payload);
	}, [runtimeEnabled, sendClientReady]);

	useEffect(() => {
		if (!runtimeEnabled) {
			return;
		}

		const sendTick = () => {
			if (hasFatalBeenReported()) {
				return;
			}

			const payload: MessagePayload<ClientToHostMessage, "HeartBeat"> = {
				instanceId: getEfRuntimeInstanceId(),
				timestamp: Date.now(),
				state: "healthy",
			};

			sendHeartBeat(payload);
		};

		sendTick();

		const id = window.setInterval(sendTick, HEARTBEAT_INTERVAL_MS);

		return () => {
			window.clearInterval(id);
		};
	}, [runtimeEnabled, sendHeartBeat]);
}

export default useEfRuntimeSupervisor;